import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useRouter } from "@tanstack/react-router";
import { LoginRequest, LoginResponse, Member, Organization } from "@/types";
import {
  login as apiLogin,
  logout as apiLogout,
  storeSessionTokens,
  getSessionJwt,
} from "@/services/auth";

interface AuthContextType {
  isAuthenticated: boolean;
  member: Member | null;
  organization: Organization | null;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [member, setMember] = useState<Member | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // Check if user is authenticated on mount
    const checkAuth = () => {
      if (typeof window === 'undefined') {
        setLoading(false);
        return;
      }

      const token = getSessionJwt();
      
      if (token) {
        // Try to get stored user data
        const storedMember = localStorage.getItem("member");
        const storedOrg = localStorage.getItem("organization");
        
        if (storedMember && storedOrg) {
          const memberData = JSON.parse(storedMember);
          const orgData = JSON.parse(storedOrg);
          setMember(memberData);
          setOrganization(orgData);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response: LoginResponse = await apiLogin(credentials);
      
      // Store tokens
      storeSessionTokens(response.sessionJwt, response.sessionToken);
      
      // Store user data
      localStorage.setItem("member", JSON.stringify(response.member));
      localStorage.setItem("organization", JSON.stringify(response.organization));
      
      setMember(response.member);
      setOrganization(response.organization);
      
      // Redirect to home
      router.navigate({ to: "/organisations" });
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const logout = () => {
    apiLogout();
    localStorage.removeItem("member");
    localStorage.removeItem("organization");
    setMember(null);
    setOrganization(null);
    
    // Navigate to login page
    router.navigate({ to: "/login" });
  };

  const contextValue = useMemo(() => {
    const authStatus = !!member && !!getSessionJwt();
    
    return {
      isAuthenticated: authStatus,
      member,
      organization,
      loading,
      login,
      logout,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member, organization, loading]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType | null {
  const context = useContext(AuthContext);
  return context || null;
}

